/**
 * Sélection de blocs à explorer activement (questions blocs proactives).
 *
 * Idée : un bloc inconnu (jamais voté, pas d'exposure) avec une bonne
 * couverture catalogue (présent dans plusieurs plats) est très utile à
 * clarifier — on apprend des préférences sur N plats d'un coup en posant
 * 1 question.
 *
 * Score d'exploration = (présence catalogue saturée) × (1 - confiance actuelle)
 *   - Présence saturée : log(1 + presence) plafonné, évite que les blocs
 *     omniprésents (riz, etc.) dominent indéfiniment
 *   - 1 - confiance : 0 si déjà voté direct ou exposure forte, 1 si neuf
 */

import { computeEffectiveBlockScores } from './effectiveBlockScores.js';

/**
 * Présence catalogue : combien de plats contiennent chaque bloc.
 */
export function computeBlockPresence(dishCatalogue) {
  const map = new Map();
  for (const dish of dishCatalogue || []) {
    const ids = new Set((dish.blocks || []).map((b) => b?.blockId).filter(Boolean));
    for (const bid of ids) {
      map.set(bid, (map.get(bid) || 0) + 1);
    }
  }
  return map;
}

/**
 * Retourne les blocs candidats à une question proactive.
 *
 * @param {object} profile
 * @param {Array} dishes
 * @param {Array} blocks
 * @param {object} options - { minPresence (default 2), topN (default 12) }
 * @returns {Array<{ id, presence, confidence, score, block }>}
 */
export function pickProactiveBlocks(profile, dishes, blocks, options = {}) {
  const minPresence = options.minPresence ?? 2;
  const topN = options.topN ?? 12;

  const prefs = profile?.blockPreferences || {};
  const directlyVoted = new Set([...(prefs.likes || []), ...(prefs.dislikes || []), ...(prefs.neutral || [])]);
  const presence = computeBlockPresence(dishes);
  const effective = computeEffectiveBlockScores(profile, dishes);
  const blockMap = new Map((blocks || []).map((b) => [b?.id, b]));

  const out = [];
  for (const block of blocks || []) {
    if (!block?.id) continue;
    if (directlyVoted.has(block.id)) continue; // déjà clarifié
    const p = presence.get(block.id) || 0;
    if (p < minPresence) continue;

    const conf = effective.get(block.id)?.confidence || 0;
    if (conf > 0.4) continue; // déjà bien connu via exposure

    // Saturation : log(1+p) plafonné à 4 (≈ 50 plats)
    const presenceFactor = Math.min(4, Math.log(1 + p)) / 4;
    const noveltyFactor = 1 - conf;
    const score = presenceFactor * noveltyFactor;

    out.push({ id: block.id, presence: p, confidence: conf, score, block });
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, topN);
}
